/**
 * ASIN-keyed image + price enrichment for the affiliate card block.
 *
 * Use this for products that appear in blog content but aren't part of any
 * article's curated picks (so they don't pollute article-product-picks.ts
 * and cause duplicate sidebar entries).
 *
 * Regenerate with:  npm run paapi:enrichment-regenerate -- --apply
 * Image IDs come from the PAAPI response ( `images.primary.large.url` ).
 */

export interface AmazonProductEnrichment {
  /** Curated editorial name. Not overwritten by the regenerator. */
  name: string
  /** Live Amazon price, refreshed by scripts/paapi-enrichment-regenerate.ts. */
  price: string
  /** Amazon CDN image identifier. Used to build a URL via `m.media-amazon.com/images/I/${imageId}._AC_SL200_.jpg`. */
  imageId?: string
  /** Full image URL (e.g. a Directus-hosted image). When set, takes precedence over imageId and signals a non-PAAPI source. */
  imageUrl?: string
  /** Raw Amazon title (cleaned). Used as a subtitle when the editorial name differs from what's actually listed. */
  amazonTitle?: string
  /** Strikethrough list price when the item is currently on sale. Omitted when there's no savings. */
  listPrice?: string
}

export const AMAZON_PRODUCT_ENRICHMENT: Record<string, AmazonProductEnrichment> = {
  B007PHNUOE: {
    name: 'SedgeHammer',
    price: '26.99',
    imageId: '51k2aBcfPuL',
    amazonTitle: 'Sedgehammer Plus Turf Herbicide, Purple & Yellow Nutsedge Killer, 2pk',
  },
  B0BTN1DPMD: {
    name: 'Celsius WG Herbicide (Easy Mix Packet)',
    price: '16.99',
    imageId: '41hwVWk83-L',
    amazonTitle: 'Celsius WG - 0.226 oz Easy Mix Packet',
  },
  B0058W42QS: {
    name: 'BASF Drive XLR8 Crabgrass Killer',
    price: '57.11',
    imageId: '41sfNjwp+0L',
    amazonTitle: 'BASF Drive XLR8 Crabgrass Killer for Lawn & Turf, Post Emergent Grassy & Broadleaf Weed Control, Act',
  },
  B004W4SMUA: {
    name: 'Certainty Turf Herbicide',
    price: '132.70',
    imageId: '41zabvjrK3L',
    amazonTitle: 'Certainty Turf Herbicide - 1.25 Ounces',
  },
  B009ATET6I: {
    name: 'Triclopyr 4EC (Turflon Ester)',
    price: '39.96',
    imageId: '41ieh5YADFL',
    amazonTitle: 'Monterey Turflon Ester 8oz',
  },
  B00XJM8KMQ: {
    name: 'Scotts Turf Builder Weed and Feed 3',
    price: '64.99',
    imageId: '41IVt6FtH2L',
    amazonTitle: 'Scotts Turf Builder Weed & Feed3, 14.29 lbs.',
  },
  B08L6T3B4H: {
    name: 'Scotts Halts Crabgrass & Grassy Weed Preventer',
    price: '39.13',
    imageId: '5196bQGYE8L',
    amazonTitle: 'Scotts Halts Crabgrass & Grassy Weed Preventer, 20.12 lbs.',
    listPrice: '43.49',
  },
  B09JVFT7N5: {
    name: 'Scotts Turf Builder Triple Action',
    price: '36.74',
    imageId: '51in7bV-LRL',
    amazonTitle: 'Scotts Turf Builder Triple Action1, 11.31 lb.',
    listPrice: '45.99',
  },
  B0058V1IV6: {
    name: '2,4-D Amine Selective Herbicide (Hi-Yield)',
    price: '32.38',
    imageId: '41mkJXd+iyL',
    amazonTitle: 'Hi-Yield (21416) 2,4-D Selective Weed Killer, 1 gal',
  },
  B015BSWOV8: {
    name: 'PBI/Gordon Trimec Classic Herbicide',
    price: '82.88',
    imageId: '41UgRCjJAFL',
    amazonTitle: 'PBI / Gordon Trimec Classic Herbicide 1 Gal Post-Emergent for All Major Broadleaf Weeds',
  },
  B001PCRKDC: {
    name: 'Gordon\'s SpeedZone Lawn Weed Killer',
    price: '35.75',
    imageId: '411cLnK5YAS',
    amazonTitle: 'Gordon’s SpeedZone Lawn Weed Killer, 20 Ounces',
  },
  B015BSNZP2: {
    name: 'Prodiamine 0-0-7',
    price: '77.89',
    imageId: '41YJyZ6Jo5L',
    amazonTitle: 'Prodiamine 65 Wdg 5lbs Pre-emergent Grass Broadleaf Weeds ( Generic Barricade )',
  },
  B0051GXXPW: {
    name: 'Dimension 2EW',
    price: '156.96',
    imageId: '41Tp97DV1QL',
    amazonTitle: 'Dimension 2EW Herbicide - 0.5 Half Gallon (64 ounces)',
  },
  B00OXD2CKG: {
    name: 'Barricade 4FL Herbicide Concentrate (4 Fl Oz)',
    price: '29.99',
    imageId: '41D4yJD+pcL',
    amazonTitle: 'Barricade 4FL Herbicide Concentrate - Preemergent Weed Control - Long Lasting Broadleaf Weed Prevent',
  },
  B09SBR4BBR: {
    name: 'Tenacity Herbicide',
    price: '29.99',
    imageId: '315ZvYGOjtL',
    amazonTitle: 'Atticus Torocity Mesotrione Herbicide (8oz) - (Compare to Tenacity)',
  },
  B09NCJ2ZXQ: {
    name: 'Roundup for Lawns Concentrate',
    price: '20.46',
    imageId: '41P9IJ+x2sL',
    amazonTitle: 'Roundup for Lawns2 Concentrate, 32 fl. oz.',
  },
  B006LAVM3W: {
    name: 'Quinclorac 75DF',
    price: '94.66',
    imageId: '31FUKs3d3uL',
    amazonTitle: 'Quali-Pro Quinclorac 75 DF Selective Herbicide 1lb',
  },
  B07NZ2BMMK: {
    name: 'The Andersons Barricade Professional-Grade Prodiamine',
    price: '46.88',
    imageUrl: 'https://directus-production-70c5.up.railway.app/assets/9ad17c45-dee7-4a24-8959-4202c7817e1b',
    amazonTitle: 'The Andersons Barricade Professional-Grade Granular Pre-Emergent Weed Control - Covers up to 5,800 s',
  },
  B000UGU59K: {
    name: 'Scotts Turf Builder Southern Lawn Food',
    price: '63.98',
    imageUrl: 'https://directus-production-70c5.up.railway.app/assets/d55d8394-57c6-4fdd-9695-ab07df4cf924',
    amazonTitle: 'Scotts Turf Builder Southern Lawn Food, 42.18 lbs.',
  },
  B09FYVJL56: {
    name: 'Greenworks 13 Amp Corded Electric Dethatcher/Scarifier',
    price: '159.00',
    imageUrl: 'https://directus-production-70c5.up.railway.app/assets/2822b4ff-e856-4de3-9b8d-faf8f6b06f31',
    amazonTitle: 'Greenworks 13 Amp 14-Inch Corded Dethatcher / Scarifier, 2-in-1 Electric Lawn Rake with 3-Position D',
    listPrice: '219.99',
  },
  B07YM583XL: {
    name: 'Ortho WeedClear Lawn Weed Killer Ready-to-Spray',
    price: '12.99',
    imageUrl: 'https://directus-production-70c5.up.railway.app/assets/5ec971b9-5c2c-4a7f-9aea-1a8581ff9eb5',
    amazonTitle: 'Ortho WeedClear Lawn Weed Killer Ready-to-Spray, 32 fl. oz.',
    listPrice: '15.49',
  },
  B002YPS1KK: {
    name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
    price: '41.57',
    imageUrl: 'https://directus-production-70c5.up.railway.app/assets/227e0df2-bbe4-49d7-95ff-73adff8f1ad5',
    amazonTitle: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
  },
  B004XDHRCE: {
    name: 'Southern Ag Surfactant for Herbicides Non-Ionic, 16oz, 1 Pint',
    price: '14.32',
    imageId: '51fBawyDg7L',
    amazonTitle: 'Southern Ag Surfactant for Herbicides Non-Ionic, 16oz, 1 Pint',
  },
  B0B9R12G96: {
    name: 'Ortho GroundClear Poison Ivy & Tough Brush Killer3, 1 gal.',
    price: '20.37',
    imageId: '41qzS-yThGL',
    amazonTitle: 'Ortho GroundClear Poison Ivy & Tough Brush Killer3, 1 gal.',
    listPrice: '30.99',
  },
  B07GL6V229: {
    name: 'SHOWA 730 Chemical Resistant Nitrile Gloves (Large)',
    price: '6.22',
    imageId: '31Cv63DhuML',
    amazonTitle: 'SHOWA 730 Chemical Resistant Glove, 15-mil Nitrile, Large, Single Pair',
  },
  B08WWY8KQK: {
    name: 'ACCUPOUR 8oz Measuring Pitcher',
    price: '11.99',
    imageId: '31u9Vc0+VJL',
    amazonTitle: 'ACCUPOUR 8oz (250 mL) Measuring Pitcher, Plastic, Multipurpose - Great for Chemicals, Oil, Pool and ',
  },
  B0050DV4ZW: {
    name: 'Scotts GrubEx1 Season Long Grub Killer, 5,000 sq. ft.',
    price: '29.47',
    imageId: '41vLRIqeMiL',
    amazonTitle: 'Scotts GrubEx1 Season Long Grub Killer, 5,000 sq. ft.',
    listPrice: '31.99',
  },
  B0052MZBYQ: {
    name: 'Southern Ag 006130 Atrazine St. Augustine Weed Killer 32oz Specialty Herbicide',
    price: '16.44',
    imageId: '41xJ0HypHcL',
    amazonTitle: 'Southern Ag 006130 Atrazine St. Augustine Weed Killer 32oz Specialty Herbicide, Light Tan',
  },
  B007PHMAYK: {
    name: 'Sedgehammer Plus Turf Solution 13.5grams',
    price: '13.99',
    imageId: '51w5G6PEFFL',
    amazonTitle: 'Sedgehammer Plus Turf Solution, 13.5 Grams, Nutsedge Control',
    listPrice: '14.89',
  },
  B0F2BZX4YR: {
    name: 'Chapin 20084 1-Gallon Pump Sprayer (4-in-1 Nozzle)',
    price: '24.95',
    imageId: '41Mbdp1ifZL',
    amazonTitle: 'Chapin 20084: 1 Gallon Pump Sprayer – Lawn & Garden Sprayer for Weeds, Pesticides & More – 4-in-1 Sp',
  },
  B07SYLCKRH: {
    name: 'The Andersons PGF Complete 16-4-8',
    price: '59.88',
    imageId: '41xelE+7eJL',
    amazonTitle: 'The Andersons Professional PGF Complete 16-4-8 Fertilizer with 7% Humic DG 5,000 sq ft',
  },
  B06XWFL84R: {
    name: 'Simple Lawn Solutions Advanced 16-4-8 Liquid Fertilizer',
    price: '23.77',
    imageId: '419+STgxhSL',
    amazonTitle: 'Advanced 16-4-8 Balanced NPK - Lawn Food Quality Liquid Fertilizer - Spring & Summer Concentrated Sp',
  },
  B0DYWRC5NM: {
    name: 'Pennington Full Season Lawn Fertilizer 32-0-5',
    price: '27.99',
    imageId: '51wvj2XoEQL',
    amazonTitle: 'Pennington Full Season Lawn Fertilizer 4M 32-0-5 RF 3/11.2Lb',
  },
  B0B7ZZDNQZ: {
    name: 'Pennington Fast Acting Lime 40 lb',
    price: '',
  },
  B07YTHBHRD: {
    name: 'Espoma Organic Garden Lime 6.75 lb',
    price: '',
    imageId: '41rMBHmOUdL',
    amazonTitle: 'B12Y19D01W087844M02T07C19S08US-C - Men\'s Flexfit Baseball Hat Cap, White, Large/X-Large',
  },
  B00BIIA9RA: {
    name: 'Hi-Yield Wettable Dusting Sulfur 4 lb',
    price: '',
  },
  B0052WNSGE: {
    name: 'Espoma Organic Soil Acidifier 6 lb',
    price: '',
  },
  B07M69M51Q: {
    name: 'Apera Instruments pH20 Waterproof pH Pen',
    price: '',
  },
  B00AA8XUYW: {
    name: 'Rain Bird RRBC Sprinkler Catch Cup Test Kit',
    price: '',
  },
  B07VGCP99Q: {
    name: 'Orbit 56175 Sprinkler System Pressure Gauge',
    price: '',
  },
}
